import React, { useEffect } from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import type { Position, TestState } from '@merels/web/deps/debug/positions/positions'
import { makeTestStateSelectorState } from './testStateSelector.state'
import { StateSearch } from './StateSearch'
import { StateListView } from './StateListView'
import { useWebContainer } from '../../../providers/DepProvider/DepProvider'

interface TestStateSelectorProps {
  gameId: string
  availableStates: Position[]
  onApplyState: (state: { G?: any; ctx?: any }, stateName?: string, stateDisplayName?: string) => Promise<boolean>
  initialStateName?: string
}

export function TestStateSelector({
  gameId,
  availableStates,
  onApplyState,
  initialStateName
}: TestStateSelectorProps) {
  useSignals()
  
  const container = useWebContainer()
  const state = React.useMemo(() => makeTestStateSelectorState(gameId, container.gameRegistry), [gameId, container])

  // Load sections when gameId changes
  useEffect(() => {
    state.loadSections(gameId)
  }, [gameId, state])

  // When filtering, all sections should be expanded
  useEffect(() => {
    if (state.searchText.value.trim() !== '') {
      state.expandAllSections()
    }
  }, [state.searchText.value, state])

  const handleApplyState = React.useCallback(async (
    stateToApply: { G?: any; ctx?: any },
    stateName?: string,
    stateDisplayName?: string
  ) => {
    state.setError(null)
    state.setIsApplying(true)
    
    try {
      const success = await onApplyState(stateToApply, stateName, stateDisplayName)
      if (!success) {
        state.setError('Failed to apply state - make sure a game is started first')
        return false
      } else {
        state.setIsEditing(false)
        if (stateName) {
          state.markStateAsUsed(stateName)
        }
        return true
      }
    } catch (e) {
      state.setError('Error applying state: ' + (e as Error).message)
      return false
    } finally {
      state.setIsApplying(false)
    }
  }, [gameId, onApplyState, state])

  // Load initial state from URL if provided
  useEffect(() => {
    if (initialStateName && availableStates.length > 0 && !state.loadedStateName.value && gameId) {
      const foundState = availableStates.find(s => s.name === initialStateName)
      if (foundState) {
        state.setSelectedState(foundState)
        state.setLoadedStateName(foundState.name)
        handleApplyState(foundState.state, foundState.name, foundState.displayName)
      }
    }
  }, [initialStateName, availableStates, state.loadedStateName.value, handleApplyState, gameId, state])

  const handleStateClick = async (clickedState: TestState) => {
    state.setSelectedState(clickedState)
    state.setError(null)
    
    // Apply state immediately
    const success = await handleApplyState(clickedState.state, clickedState.name, clickedState.displayName)
    
    if (success) {
      state.setLoadedStateName(clickedState.name)
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Update URL
      const roomName = window.location.pathname.match(/\/r\/([^\/]+)/)?.[1]
      if (roomName) {
        const newPath = `/r/${roomName}/${encodeURIComponent(gameId)}/${encodeURIComponent(clickedState.name)}`
        window.history.pushState(null, '', newPath)
      }
    }
  }

  if (state.sections.value.length === 0) {
    return (
      <div className="text-sm text-muted p-2">
        No positions available for {gameId}
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Search box */}
      <StateSearch
        value={state.searchText.value}
        onChange={state.setSearchText}
      />

      {/* Error message */}
      {state.error.value && (
        <div className="p-2 bg-danger-bg/20 border-2 border-danger-bg/40 text-danger text-sm flex-shrink-0">
          {state.error.value}
        </div>
      )}

      {/* States list */}
      <div className="flex-1 overflow-auto">
          <StateListView
            sections={state.filteredSections.value}
            expandedSections={state.expandedSections.value}
            searchText={state.searchText.value}
            selectedStateName={state.selectedState.value?.name || null}
            showJsonViewer={false}
            onToggleSection={state.toggleSection}
            onStateClick={handleStateClick}
          />
      </div>
    </div>
  )
}
